import type { WindowStartBasis } from "@nfp-tracker/utils";

/**
 * Plain-language copy for the Insights "Fertile Window" stat. Display only: the basis and day
 * come from computeCycleStats / computeFertileWindow and are never re-derived here.
 */

/** Short "how it opens" line, e.g. "Opens CD 6 · standard Day 6 protocol". */
export const describeWindowOpening = (basis: WindowStartBasis, startCd: number | null): string => {
  const cd = startCd ? `CD ${startCd}` : null;
  switch (basis) {
    case "mucus-onset":
      return "Opens at first mucus";
    case "monitor-high":
      return `Opens ${cd ?? "early"} · early High monitor readings`;
    case "shortest-cycle-minus-20":
      return `Opens ${cd ?? "early"} · shortest cycle − 20`;
    case "protocol-day-6":
      return `Opens ${cd ?? "CD 6"} · standard Day 6 protocol`;
    case "postpartum":
      return "Opens CD 1 · postpartum: no infertile days before Peak";
    case "start-not-recorded":
      return "Opens from the first logged day · cycle start not recorded";
  }
};

/** One sentence explaining the opening rule that applied. */
export const describeWindowRule = (basis: WindowStartBasis): string => {
  switch (basis) {
    case "mucus-onset":
      return "Your window opens on the first day any mucus is noticed after your period.";
    case "monitor-high":
      return "Your window opened early because the monitor read High on consecutive days before the usual start day.";
    case "shortest-cycle-minus-20":
      return "Your window opens on the length of your shortest recent cycle minus 20 days (from your last 6 cycles).";
    case "protocol-day-6":
      return "Your window opens on Day 6, the standard start used until there's enough cycle history for the shortest − 20 rule.";
    case "postpartum":
      return "While postpartum no days are assumed infertile, so the window counts as open from CD 1 until it closes after Peak.";
    case "start-not-recorded":
      return "Without a recorded cycle start no calendar rule can apply, so the window counts as open from the first logged day.";
  }
};
